/**
 * useEmailFilters — exposes the current user's filter bindings + the messages
 * for whichever binding is active. Re-fetches messages whenever active
 * binding id changes.
 */
import { useCallback, useEffect, useState } from 'react';
import type { EmailFilterBinding, EmailMessage } from '../lib/email';
import { listBindings, listMessages, triggerEmailSync } from '../lib/email';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

export function useEmailFilters() {
  const { tenant } = useTenant();
  const { session } = useAuth();
  const tenantId = tenant?.id ?? null;
  const userId = session?.user?.id ?? null;

  const [bindings, setBindings] = useState<EmailFilterBinding[]>([]);
  const [activeBindingId, setActiveBindingId] = useState<string | null>(null);
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [bindingsLoading, setBindingsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBindings = useCallback(async () => {
    if (!tenantId) return;
    setBindingsLoading(true);
    setError(null);
    try {
      const data = await listBindings(tenantId);
      const mine = userId ? data.filter((b) => b.user_id === userId && b.is_active) : data;
      setBindings(mine);
      if (mine.length > 0 && !mine.some((b) => b.id === activeBindingId)) {
        setActiveBindingId(mine[0].id);
      } else if (mine.length === 0) {
        setActiveBindingId(null);
      }
    } catch (err) {
      setError((err as Error).message);
      setBindings([]);
    } finally {
      setBindingsLoading(false);
    }
  }, [tenantId, userId, activeBindingId]);

  useEffect(() => {
    void loadBindings();
  }, [loadBindings]);

  useEffect(() => {
    if (!activeBindingId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setMessagesLoading(true);
      try {
        const data = await listMessages(activeBindingId, 20);
        if (!cancelled) setMessages(data);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setMessages([]);
        }
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeBindingId]);

  const refresh = useCallback(async () => {
    try {
      await triggerEmailSync(activeBindingId ?? undefined);
    } catch (err) {
      console.warn('[useEmailFilters] sync failed:', (err as Error).message);
    }
    await loadBindings();
    try {
      if (activeBindingId) {
        const data = await listMessages(activeBindingId, 20);
        setMessages(data);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, [activeBindingId, loadBindings]);

  return {
    bindings,
    activeBindingId,
    setActiveBindingId,
    messages,
    bindingsLoading,
    messagesLoading,
    error,
    refresh,
    reloadBindings: loadBindings,
  };
}
